<template>
  <div class="help-page ma-6">
    <h1 style="padding: 0 0 0.5em 0" class="itemis-analyze-headline">
      Overview of itemis ANALYZE
    </h1>
    <p style="padding: 0 0 1em 0"
      >ANALYZE enables easy and well-arranged traceability management across all tools. It connects
      to the tools involved in the development process by means of custom tool adapters which are
      able to access the data-representing artifacts of the development process and related
      traceability information. These adapters do not only read or write data from those tools, they
      even support navigation across tools. As an example, by means of ANALYZE, an Autosar component
      designed in Enterprise Architect is only two clicks away from its specification in PTC
      Integrity (and vice versa).
    </p>

    <h2 style="padding: 0 0 1em 0">Functional overview of itemis ANALYZE</h2>
    <p style="padding: 0 0 1em 0"
      >ANALYZE offers several possibilities to evaluate the data gathered from connected tools and
      the link information stored in ANALYZE itself, e.g., in the form of a coverage report or a
      validation of the links. As ANALYZE unifies access to development artifacts across the whole
      tool chain, it supports you in various use cases of your daily work:
    </p>

    <ul style="padding: 0 0 1em 0">
      <li> Tracing: managing links across all tools </li>
      <li> Relation overview: browsing and navigating existing links </li>
      <li> Validations: identifying project changes </li>
      <li>Analysis: observing the traceability status</li>
      <li>Exports: creating a snapshot</li>
    </ul>
    <h3 style="padding: 0 0 1em 0">Tracing: managing links across all tools</h3>
    <p style="padding: 0 0 1em 0"
      >ANALYZE combines several approaches to manage associations between development artifacts:</p
    >
    <ul style="padding: 0 0 1em 0">
      <li
        >It supports non-invasive tracing, i.e., it stores the trace information in its own store
        and does not change the original data sources.</li
      >
      <li
        >Some adapters, such as the Eclipse CDT adapter, Enterprise Architect adapter, MS Excel
        adapter, or text file adapter also support invasive tracing, which means that ANALYZE stores
        link information in the original data source, e.g., it adds a comment to a C function
        holding the ID of a linked requirement.</li
      >
      <li
        >It is able to adopt link information from the tools involved, e.g., a link between IBM
        Rational DOORS requirements or a relation between UML artifacts may be adopted as
        traceability links.</li
      >
      <li
        >It can also derive links from matching attributes of artifacts originating from different
        tools, e.g., if the test case ID of a test result provided in an XML file matches the ID of
        a test case specified in an Excel sheet. Dependent on development process and tools
        involved, ANALYZE enables you to fully configure which types of artifacts are relevant and
        how the links between those artifacts are managed.</li
      >
    </ul>
    <p style="padding: 0 0 1em 0"
      >For those links whose lifecycle is managed by ANALYZE (i.e., neither derived nor adopted, but
      stored by it), several ANALYZE functions ease link creation: ANALYZE recognizes selections in
      external tools and shows the selected artifacts in a selection history it is possible to
      search for specific artifacts of different tools and you can store often-used artifacts in a
      favorites list and then re-use them for creating links.
    </p>
    <p style="padding: 0 0 1em 0"
      >For those links which are derived based on attributes, it is possible to create links by
      manipulating the original data in their specific tools. Imagine a test case in Excel with an
      identifier in one column and a concatenation of identifiers of related requirements in another
      column. If a so-called attribute mapping is configured, ANALYZE iterates over these columns
      and – if the attributes match – creates links between the test cases and the requirements,
      independent of the tool the requirements originate from. This means that you do not have to
      switch to ANALYZE to create any links, but instead you can work in, say, Excel and let ANALYZE
      derive the links automatically. The same is possible with the Eclipse CDT adapter.
    </p>
    <p style="padding: 0 0 1em 0"
      >As artifacts and circumstances can change during work, it is sometimes necessary to change
      existing links. ANALYZE allows you to
    </p>
    <ul style="padding: 0 0 1em 0">
      <li>delete one or more links directly,</li>
      <li>change the link end(s) of a specific link,</li>
      <li
        >replace an artifact in the traceability graph completely (i.e., replace it in all links by
        another artifact).</li
      >
    </ul>
    <h3 style="padding: 0 0 1em 0">Relation overview: browsing and navigating existing links</h3>
    <p style="padding: 0 0 1em 0"
      >ANALYZE also provides you with different visualizations of existing links:
    </p>

    <ul style="padding: 0 0 1em 0">
      <li
        >The ANALYZE Explorer shows you all existing links in a tree structure based on different
        groupings, e.g., based on the type of artifacts or based on link types. You can expand the
        various nodes to see what is linked to what.</li
      >
      <li
        >The ANALYZE Overview displays a graph of the artifact you selected elsewhere in ANALYZE
        together with the artifacts it is linked to directly or indirectly, up to a certain depth.
        Selecting a different node in the graph focuses on a different artifact and displays its
        links. The ANALYZE overview supports various different viewing modes and options. For
        example, you can change the depth of the graph, e.g., to restrict it to only display
        directly-linked neighbors of the selected artifact, or alternatively to not only see linked
        artifacts, but also linked artifacts of linked artifacts.</li
      >
    </ul>
    <p style="padding: 0 0 1em 0"
      >As it is often necessary to edit some artifacts or to check for their correctness, ANALYZE
      offers you an easy way to navigate artifacts: In all views displaying artifacts, you can
      double click on an artifact to open and select it in its native tool.</p
    >
    <h3 style="padding: 0 0 1em 0">Validations: identifying project changes</h3>
  </div>
</template>

<script lang="ts" setup></script>

<style lang="scss" scoped>
  .help-page {
    height: 100%;
    overflow: scroll;
  }
</style>
